import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoMenu } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import {
  AdminPageUrl,
  DiagnosisPageUrl,
  LoginPageUrl,
  MyPageUrl,
  StuffPageUrl,
} from '../../url/poenUrl';
import PcLogo from '../../assets/images/PcLogo.svg';
import logoB from '../../assets/images/logoB.svg';
import { LuUser } from 'react-icons/lu';
import { FiBell } from 'react-icons/fi';
import AuthService from '../../api/service/auth/AuthService';
import NotificationService from '../../api/service/member/NotificationService';
import NotificationPopup from '../../components/NotificationPopup';

const HeaderContainer = styled.header`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 110px;
  padding: 20px;
  background-color: #fff;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  @media (max-width: 968px) {
    flex-direction: column;
    justify-content: center;
    height: 70px;
    width: 100%;
    padding: 40px 20px;
    gap: 8px;
  }
`;

const HeaderBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  width: 100%;
  @media (max-width: 968px) {
    gap: 10px;
    justify-content: space-between;
  }
`;

const MenuButton = styled(IoMenu)`
  display: none;
  font-size: 24px;
  cursor: pointer;
  @media (max-width: 968px) {
    display: block;
  }
`;

const Logo = styled.img`
  position: absolute;
  width: 150px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  @media (max-width: 1280px) {
    left: 15%;
  }
  @media (max-width: 968px) {
    display: none;
  }
`;

const MLogo = styled.img`
  display: none;
  @media (max-width: 968px) {
    display: block;
    width: 70px;
  }
`;

const Info = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  @media (max-width: 968px) {
    gap: 10px;
  }
`;

const UserInfo = styled.p`
  align-items: center;
  display: flex;
  gap: 5px;
  font-size: 16px;
  @media (max-width: 968px) {
    font-size: 10px;
  }
`;

const Circle = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background-color: #54c79b;
`;

const LogoutBotton = styled.button`
  padding: 5px 10px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.textColor};
  background-color: ${(props) => props.theme.colors.grayColor};
  border: none;
  border-radius: 5px;
  &:hover {
    font-weight: 700;
  }
  @media (max-width: 968px) {
    display: none;
  }
`;

const FiBellIcon = styled(FiBell)`
  font-size: 20px;
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: ${(props) => props.theme.colors.mainColor};
  color: white;
  font-size: 8px;
  font-weight: 600;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LuUserIcon = styled(LuUser)`
  font-size: 20px;
`;

const Header: React.FC<{ toggleSidebar: () => void }> = ({ toggleSidebar }) => {
  const navigate = useNavigate(); // 리디렉션을 위한 useNavigate 훅 사용
  const [name, setName] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0); // 읽지 않은 알림 개수 상태
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

  const handleNotificationClick = () => {
    setIsPopupOpen(true); // 팝업 열기
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false); // 팝업 닫기
  };

  useEffect(() => {
    const fetchUserData = () => {
      const storedName = localStorage.getItem('name');
      const storedIdentifier = localStorage.getItem('identifier');
      const storedPermissions = localStorage.getItem('permissions');

      setName(storedName);
      setIdentifier(storedIdentifier);

      if (storedPermissions === 'ADMIN') {
        setIsAdmin(true);
      }
    };

    const fetchUnreadCount = async () => {
      try {
        const response =
          await NotificationService.getUnreadNotificationsCount();
        setUnreadCount(response.data); // 알림 개수 업데이트
      } catch (error) {
        console.error('읽지 않은 알림 개수 조회 실패:', error);
      }
    };

    fetchUserData();
    fetchUnreadCount(); // 컴포넌트가 마운트될 때 알림 개수 가져오기
  }, []);

  const handleLogout = async () => {
    try {
      const response = await AuthService.logout(); // AuthService의 logout 메서드 호출
      alert(response.resultMsg);
      navigate(LoginPageUrl); // 로그아웃 후 로그인 페이지로 리디렉션
    } catch (error) {
      alert(error);
      console.error('로그아웃 실패:', error);
    }
  };

  const handleProfileClick = () => {
    const storedPermissions = localStorage.getItem('permissions');

    if (storedPermissions === 'ADMIN') {
      navigate(AdminPageUrl); // 관리자 페이지로 이동
    } else {
      navigate(MyPageUrl); // UserProfilePageUrl로 이동
    }
  };

  const handleLogoClick = () => {
    const storedPermissions = localStorage.getItem('permissions');
    if (storedPermissions === 'ADMIN') {
      navigate(AdminPageUrl); // 관리자 페이지로 이동
    } else if (storedPermissions === 'SELLER') {
      navigate(DiagnosisPageUrl); // 사용자 페이지로 이동
    } else if (storedPermissions === 'BUYER') {
      navigate(StuffPageUrl); // 사용자 페이지로 이동
    }
  };

  return (
    <HeaderContainer>
      <HeaderBox>
        <Logo
          src={PcLogo}
          alt="전기차 배터리 시스템"
          onClick={handleLogoClick}
        />
        <MLogo src={logoB} alt="Logo" onClick={handleLogoClick} />
        <LogoutBotton onClick={handleLogout}>로그아웃</LogoutBotton>
        <Info>
          <UserInfo>
            <Circle />
            {isAdmin
              ? '관리자'
              : name && identifier
                ? `${name}(${identifier})`
                : '로그인 필요'}
          </UserInfo>
          <div
            style={{ position: 'relative' }}
            onClick={handleNotificationClick}
          >
            <FiBellIcon />
            {unreadCount > 0 && (
              <NotificationBadge>{unreadCount}</NotificationBadge>
            )}
          </div>
          <LuUserIcon onClick={handleProfileClick} /> {/* 클릭 이벤트 추가 */}
          <MenuButton onClick={toggleSidebar} />
        </Info>
      </HeaderBox>
      {isPopupOpen && <NotificationPopup onClose={handleClosePopup} />}
    </HeaderContainer>
  );
};

export default Header;
